/**
 * Business Central app-only (S2S / client-credentials) connection utilities.
 *
 * Unlike the delegated OAuth flow this replaced, there is no browser redirect and no per-lab
 * secret. Milva's own app registration authenticates as itself against the lab's Microsoft Entra
 * tenant. Setup is two one-time actions taken by the lab's own IT/BC admin, outside Milva
 * entirely:
 *  1. Open the admin consent link (built from the lab's Tenant ID) to let Milva's app exist in
 *     their tenant.
 *  2. Register Milva's Client ID as a security principal in Business Central Admin Center
 *     (Users → "Azure Active Directory Applications") and assign it a permission set.
 *
 * The lab enters Tenant ID / Environment Name / Company ID directly (found in their own Azure
 * Portal / Business Central Admin Center) and clicks "Verificer forbindelse" — there is no
 * "Connect" redirect button any more.
 */

import type {
  TCustomerInvoicingData,
  TInsuranceCustomerAccountInvoicingData,
} from '@team-milva/lab-models/invoicing'
import type { TInvoiceSyncStatus } from '@team-milva/lab-models/invoice'
import type { TFinalInvoicePayer, TPaymentStatus } from '@team-milva/lab-models/resources'
import type { TStatusChipVariant } from '@team-milva/lab-components'
import type { TOrderInvoiceBlockReason, TOrderFinalInvoiceStatus } from '@team-milva/lab-utils/orderInvoiceBlocking'
import { managementApi } from './api/managementClient'
import { t } from '@/locales'
import { parseBcErrorTag } from '@/globals/typescript/utils/businessCentralErrors'

// `/lab-accounts/{labAccountID}/bc-auth/status` still needs the untyped client: its optional
// ?tenantID=&environmentName=&companyID= override query params aren't part of the generated
// request type (only the path param is). `/lab-accounts/{labAccountID}/bc-auth/client-id` also
// needs it — its lab-cloud schema never declared a requestParamsSchema (the endpoint isn't
// actually lab-scoped, see fetchBcClientID below), so the generated type has no path param at
// all even though the route is nested under this router's `:labAccountID` prefix. Everything
// else on this client is fully typed.
type TUntypedApiClient = (
  method: string,
  path: string,
  opts?: Record<string, unknown>
) => Promise<{ success: boolean; data: Record<string, unknown> | null; message?: string }>

const untypedManagementApi = managementApi as unknown as TUntypedApiClient

// ---------------------------------------------------------------------------
// Verify-connection result messages
// ---------------------------------------------------------------------------

export const BcConnectionIssue = {
  NOT_CONFIGURED: 'not-configured',
  CONSENT_REQUIRED: 'bc-consent-required',
  ENTRA_ERROR: 'entra-error',
  BC_NOT_FOUND: 'bc-not-found',
  BC_PERMISSION_SET_MISSING: 'bc-permission-set-missing',
  BC_ERROR: 'bc-error',
} as const

export type TBcConnectionIssue = (typeof BcConnectionIssue)[keyof typeof BcConnectionIssue]

/** Parses the `message` field returned by the verify-connection endpoint into a known issue kind. */
export function parseBcConnectionIssue(message: string | undefined): {
  issue: TBcConnectionIssue | null
  detail: string
} {
  if (!message) return { issue: null, detail: '' }
  const [prefix = '', ...rest] = message.split(':')
  const detail = rest.join(':')
  if ((Object.values(BcConnectionIssue) as string[]).includes(prefix)) {
    return { issue: prefix as TBcConnectionIssue, detail }
  }
  return { issue: null, detail: message }
}

/**
 * The human sentence for a connection issue. Two surfaces say why a connection does not work —
 * the setup wizard, step by step, and the settings page's broken-connection banner — and they
 * have to say the same thing, so the mapping lives here rather than once per surface.
 *
 * BC's and Entra's own error text is appended only for the three issues where it names something
 * actionable (a missing permission set, an Entra or BC error); the rest already read as an
 * instruction, and raw API text under them is noise.
 */
export function buildBcIssueMessage(
  issue: TBcConnectionIssue | null,
  issueDetail: string
): string {
  const showRawDetail =
    issue === BcConnectionIssue.ENTRA_ERROR ||
    issue === BcConnectionIssue.BC_ERROR ||
    issue === BcConnectionIssue.BC_PERMISSION_SET_MISSING
  const message = (() => {
    switch (issue) {
      case BcConnectionIssue.NOT_CONFIGURED:
        return t('area.business-central.auth.message.issue.not-configured')
      case BcConnectionIssue.CONSENT_REQUIRED:
        return t('area.business-central.auth.message.issue.consent-required')
      case BcConnectionIssue.ENTRA_ERROR:
        return t('area.business-central.auth.message.issue.entra-error')
      case BcConnectionIssue.BC_NOT_FOUND:
        return t('area.business-central.auth.message.issue.bc-not-found')
      case BcConnectionIssue.BC_PERMISSION_SET_MISSING:
        return t('area.business-central.auth.message.issue.bc-permission-set-missing')
      case BcConnectionIssue.BC_ERROR:
        return t('area.business-central.auth.message.issue.bc-error')
      default:
        return t('error.common.standard')
    }
  })()
  return showRawDetail && issueDetail ? `${message} (${issueDetail.slice(0, 300)})` : message
}

// DEV: Milva's own BC test tenant + its environment — used only by the setup wizard's
// developer quick-fill buttons (gated by isDeveloper && showDevActions).
export const BC_DEV_TEST_TENANT_ID = 'db33d428-935d-4b29-a96f-6d730a159126'
export const BC_DEV_TEST_ENVIRONMENT_NAME = 'Sandbox'

export async function fetchBcClientID(labAccountID: string): Promise<string> {
  const result = await untypedManagementApi(
    'get',
    'lab-accounts/{labAccountID}/bc-auth/client-id',
    { path: { labAccountID } }
  )
  if (!result.success || !result.data) throw new Error('Failed to fetch BC client ID')
  return (result.data as { clientID: string }).clientID
}

/**
 * `redirect_uri` must be pre-registered on the Entra app registration's Authentication blade
 * (Web platform) for this exact origin — Microsoft rejects the consent otherwise. Points at a
 * plain public page with no backend involvement: there is no code to exchange and nothing to
 * validate, unlike the old delegated-OAuth callback this replaced.
 */
export function buildBcAdminConsentUrl(clientID: string, tenantID: string): string {
  const params = new URLSearchParams({
    client_id: clientID,
    redirect_uri: `${window.location.origin}/bc/admin-consent-complete`,
  })
  return `https://login.microsoftonline.com/${encodeURIComponent(tenantID)}/adminconsent?${params.toString()}`
}

export async function verifyBcConnection(
  labAccountID: string,
  override?: { tenantID?: string; environmentName?: string; companyID?: string }
): Promise<{ connected: boolean; issue: TBcConnectionIssue | null; detail: string }> {
  const result = await untypedManagementApi(
    'post',
    '/lab-accounts/{labAccountID}/bc-auth/status',
    {
      path: { labAccountID },
      body: {
        ...(override?.tenantID && { tenantID: override.tenantID }),
        ...(override?.environmentName && { environmentName: override.environmentName }),
        ...(override?.companyID && { companyID: override.companyID }),
      },
    }
  )
  if (!result.success || !result.data) return { connected: false, issue: null, detail: '' }
  const data = result.data as { connected: boolean; message?: string }
  const { issue, detail } = parseBcConnectionIssue(data.message)
  return { connected: data.connected, issue, detail }
}

export async function disconnectBcIntegration(labAccountID: string): Promise<void> {
  const result = await managementApi('post', 'lab-accounts/{labAccountID}/bc-auth/disconnect', {
    path: { labAccountID },
  })
  if (!result.success) throw new Error(result.message ?? 'Failed to disconnect BC integration')
}

// ---------------------------------------------------------------------------
// Customer mapping payload
// ---------------------------------------------------------------------------

/**
 * Builds a full `invoicing` payload for the `PATCH customer-accounts/:id` endpoint.
 * That endpoint replaces the whole `invoicing` object on write (no field-level merge) — so
 * any save that only touches the BC customer number must still carry forward the customer's
 * existing lab-level overrides (payment terms, automatic-invoicing opt-out), or they would be
 * silently wiped.
 */
export function buildBcCustomerInvoicingPayload(
  existingInvoicing: TCustomerInvoicingData | undefined,
  customerNumber: string
): TCustomerInvoicingData {
  const options = {
    ...(existingInvoicing?.options?.paymentTermsIncludesCurrentMonth !== undefined && {
      paymentTermsIncludesCurrentMonth: existingInvoicing.options.paymentTermsIncludesCurrentMonth,
    }),
    ...(existingInvoicing?.options?.isAutomaticInvoicingDisabled !== undefined && {
      isAutomaticInvoicingDisabled: existingInvoicing.options.isAutomaticInvoicingDisabled,
    }),
  }
  return {
    type: 'business-central',
    customerNumber,
    ...(existingInvoicing?.paymentTermsDays !== undefined && {
      paymentTermsDays: existingInvoicing.paymentTermsDays,
    }),
    ...(Object.keys(options).length > 0 && { options }),
  }
}

/**
 * The insurance customer account counterpart of `buildBcCustomerInvoicingPayload`, for
 * `PATCH insurance-customer-accounts/:id` — which also replaces the whole `invoicing` object. Carries
 * forward the insurance customer's own payment terms, automatic-invoicing opt-out (MIL-402
 * Phase 8), draft-only flag and case-lines format when only the BC customer number changes. It has no batching to carry.
 */
export function buildBcInsuranceCustomerAccountInvoicingPayload(
  existingInvoicing: TInsuranceCustomerAccountInvoicingData | undefined,
  customerNumber: string
): TInsuranceCustomerAccountInvoicingData {
  const options = {
    ...(existingInvoicing?.options?.paymentTermsIncludesCurrentMonth !== undefined && {
      paymentTermsIncludesCurrentMonth: existingInvoicing.options.paymentTermsIncludesCurrentMonth,
    }),
    ...(existingInvoicing?.options?.isAutomaticInvoicingDisabled !== undefined && {
      isAutomaticInvoicingDisabled: existingInvoicing.options.isAutomaticInvoicingDisabled,
    }),
    ...(existingInvoicing?.options?.isDraftOnly !== undefined && {
      isDraftOnly: existingInvoicing.options.isDraftOnly,
    }),
    ...(existingInvoicing?.options?.hasOwnerAndRequestorLines !== undefined && {
      hasOwnerAndRequestorLines: existingInvoicing.options.hasOwnerAndRequestorLines,
    }),
  }
  return {
    type: 'business-central',
    customerNumber,
    ...(existingInvoicing?.paymentTermsDays !== undefined && {
      paymentTermsDays: existingInvoicing.paymentTermsDays,
    }),
    ...(Object.keys(options).length > 0 && { options }),
  }
}

// ---------------------------------------------------------------------------
// Invoicing display helpers
//
// Shared by OrderInvoiceView (per-order invoicing tab) and InvoicesView (the "Fakturaer" list) —
// both need to show how an order's BC sync/payment status should render as a status chip.
// ---------------------------------------------------------------------------

/**
 * The order-level chip for a split order that has only one side invoiced or one side credited —
 * a state the payment status (one order-wide flag) cannot express. `null` when it does not apply:
 * anything but `'partially-sent'`, or a side that is failed / posted-not-sent / in progress, whose
 * own chip says more.
 */
export function getSplitPartialStatusChip(params: {
  finalInvoiceStatus: TOrderFinalInvoiceStatus | null
  syncStatus: TInvoiceSyncStatus | undefined
  hasCreditedSide: boolean
}): { variant: TStatusChipVariant; label: string } | null {
  if (params.finalInvoiceStatus !== 'partially-sent') {
    return null
  }
  if (params.syncStatus === 'failed' || params.syncStatus === 'posted-not-sent' || params.syncStatus === 'in-progress') {
    return null
  }
  return {
    variant: 'amber',
    label: params.hasCreditedSide ? t('W: Delvist krediteret') : t('W: Delvist sendt'),
  }
}

/**
 * The sync status an *order-level* chip should react to (see `Order.getStatusChip()`). A fully
 * credited invoice's own sync outcome is history: the order has been detached from it and sent
 * back to 'ready-to-charge', so surfacing it as failed would describe a document the order no
 * longer belongs to. Mirrors the `isCredited` check `getBcInvoiceStatusChip` does first.
 */
export function getOrderInvoiceSyncStatus(
  invoice: { statuses: { sync: TInvoiceSyncStatus }; credit?: { status: string } } | undefined
): TInvoiceSyncStatus | undefined {
  if (!invoice) return undefined
  if (invoice.credit?.status === 'completed') return undefined
  return invoice.statuses.sync
}

/**
 * Whether the BC push finished but produced only an unposted draft. `'completed'` covers both
 * "a draft exists in BC" and "posted and sent" — the two are told apart by whether BC has given
 * the document a posted number yet, the same distinction `getBcInvoiceUrl` uses to pick between
 * the posted and unposted card. Credited invoices are excluded for the same reason as
 * `getOrderInvoiceSyncStatus`.
 */
export function isOrderInvoiceUnpostedDraft(
  invoice:
    | {
        statuses: { sync: TInvoiceSyncStatus }
        result?: { invoiceNumber?: string; invoiceDraftNumber?: string }
        credit?: { status: string }
      }
    | undefined
): boolean {
  if (!invoice) return false
  if (invoice.credit?.status === 'completed') return false
  return invoice.statuses.sync === 'completed' && !invoice.result?.invoiceNumber
}

/**
 * Whether the BC push ("Send", "Create as draft", retry) may be offered for an order's payer
 * setup. Mirrors `lab-cloud`'s payer guard (`getBcPushPayerBlock`): an insurance case with no
 * payer chosen yet (`finalInvoicePayer === null`) is blocked — the order is not invoiceable until
 * someone picks who it is billed to. A partner or split order is pushed like any other: the push
 * resolves the bill-to per side (the partner's own BC number is checked by its pre-flight).
 * A non-insurance order has no payer choice at all (`null` is its normal state) and is never hidden here.
 */
export function isBcPushAllowedForPayer(options: {
  isInsuranceCase?: boolean
  finalInvoicePayer: TFinalInvoicePayer | null | undefined
}): boolean {
  const payer = options.finalInvoicePayer ?? null
  if (options.isInsuranceCase && payer === null) return false
  return true
}

/**
 * Whether the order's customer is known to have no BC customer number — `true` only when the
 * customer is loaded and has none, so a customer that simply has not loaded yet never blocks an
 * order. Feeds `getOrderInvoiceBlockReasons`'s `customerBcNumberMissing` context. Callers pass it
 * only for a lab with BC invoicing enabled: without BC there is no number to be missing.
 */
export function isOrderCustomerBcNumberMissing(params: {
  order: { segment: string; customerAccountID: string; customerUserID?: string | null; privateUserID?: string | null }
  customerAccountsByID: Record<string, { invoicing?: { customerNumber?: string } | null } | undefined>
  customerUsersByID: Record<string, { invoicing?: { customerNumber?: string } | null } | undefined>
}): boolean {
  const { order } = params
  const customer =
    order.segment === 'private'
      ? params.customerUsersByID[order.privateUserID ?? '']
      : params.customerAccountsByID[order.customerAccountID]
  if (!customer) return false
  return !customer.invoicing?.customerNumber
}

/**
 * Whether the order's insurance partner is known to have no BC customer number — `true` only when the
 * order bills the partner (payer `'partner'` or `'split'`) and the partner is loaded and has none, so
 * a partner that simply has not loaded yet never blocks an order. Feeds
 * `getOrderInvoiceBlockReasons`'s `partnerBcNumberMissing` context, passed wherever
 * `customerBcNumberMissing` is — and, like it, only for a lab with BC invoicing enabled.
 */
export function isOrderPartnerBcNumberMissing(params: {
  order: { options: { finalInvoicePayer: TFinalInvoicePayer | null | undefined }; insurancePartnerID?: string | null }
  insuranceCustomerAccountsByID: Record<string, { invoicing?: { customerNumber?: string } | null } | undefined>
}): boolean {
  const { order } = params
  if (order.options.finalInvoicePayer !== 'partner' && order.options.finalInvoicePayer !== 'split') {
    return false
  }
  const partner = params.insuranceCustomerAccountsByID[order.insurancePartnerID ?? '']
  if (!partner) return false
  return !partner.invoicing?.customerNumber
}

/**
 * Maps an invoice's sync status (if one exists) and the order's effective payment status to a
 * status chip variant + translated label. Mirrors the sync-state-overrides-payment-status
 * precedence used on both the order invoicing tab and the invoices list. Returns `null` when
 * neither state maps to a known chip — callers decide their own fallback (e.g. a default
 * "cancelled" chip with an empty label, or no chip at all).
 */
export function getBcInvoiceStatusChip(params: {
  syncStatus: TInvoiceSyncStatus | undefined
  paymentStatus: TPaymentStatus
  /** The invoice was only posted (`result.isPostedOnly`): complete on posting, never "sent" by Milva. */
  isPostedOnly?: boolean
  /** The invoice was only drafted (`result.isDraftOnly`): finished at its draft, nothing sent or posted by Milva. */
  isDraftOnly?: boolean
  /** Whether the order's most recent invoice was reversed by a posted credit memo. */
  isCredited?: boolean
  /** From `getOrderInvoiceBlockReasons(order)`. Only checked when `paymentStatus` is
   *  'ready-to-charge' — that's the only status where "the invoice can be sent" is being
   *  claimed, so it's the only one a block reason should override. */
  blockReasons?: TOrderInvoiceBlockReason[]
}): { variant: TStatusChipVariant; label: string } | null {
  // Checked before everything else: a credited invoice sends the order back to
  // 'ready-to-charge', so the payment status alone would render it indistinguishable from an
  // order that was simply never invoiced. Stops applying by itself once the order is invoiced
  // again, since that puts it on a new, uncredited invoice.
  if (params.isCredited) {
    return { variant: 'muted', label: t('W: Krediteret') }
  }
  // Posted in BC, send never confirmed: its own state, never "sent" and never plain "failed" —
  // the only way on is "Send igen" (see `TInvoiceSyncStatus`).
  if (params.syncStatus === 'posted-not-sent') {
    return { variant: 'amber', label: t('W: Bogført, ikke sendt') }
  }
  if (params.syncStatus === 'failed') {
    return { variant: 'red', label: t('area.business-central.invoice.chip.failed') }
  }
  if (params.syncStatus === 'in-progress') {
    return { variant: 'amber', label: t('area.business-central.invoice.chip.in-progress') }
  }
  if (
    params.paymentStatus === 'ready-to-charge' &&
    params.blockReasons &&
    params.blockReasons.length > 0
  ) {
    // Just "failed" — the chip is too small for a reason; every reason is spelled out in the
    // order's Faktura tab instead (OrderInvoiceView's block-reason notice).
    return { variant: 'red', label: t('area.business-central.invoice.chip.failed') }
  }

  switch (params.paymentStatus) {
    case 'not-required':
      return { variant: 'muted', label: t('area.order.invoice.payment-status.not-required') }
    case 'awaiting-fulfillment':
      return { variant: 'blue', label: t('area.order.invoice.payment-status.awaiting-fulfillment') }
    case 'ready-to-charge':
      return { variant: 'green', label: t('area.order.invoice.payment-status.ready-to-charge') }
    case 'invoice-sent':
      // A draft-only invoice reaches the same terminal status a booked one does, but nothing is
      // booked or sent — the chip says what actually happened.
      if (params.isDraftOnly) {
        return { variant: 'green', label: t('W: Kladde oprettet') }
      }
      return params.isPostedOnly
        ? { variant: 'green', label: t('W: Bogført') }
        : { variant: 'green', label: t('area.order.invoice.payment-status.invoice-sent') }
    default:
      return null
  }
}

/** BC web client page IDs for the document cards a deep link can open. */
export const BC_PAGE_POSTED_SALES_INVOICE = 132
export const BC_PAGE_POSTED_SALES_CREDIT_MEMO = 134
/** The unposted "Sales Invoice" card — where a draft created by Milva lives until it is posted. */
export const BC_PAGE_SALES_INVOICE = 43

/**
 * Builds a BC web client deep link to a posted document (invoice or credit memo) by its
 * document number. Returns '' when the connection snapshot is incomplete (e.g. invoices created
 * before `bcConnection` was captured) — callers hide the link in that case.
 *
 * No `company` parameter: the web client only accepts the company *display name*, which is not
 * stored — BC opens the user's default company, which is correct for single-company tenants.
 */
export function buildBcDocumentUrl(params: {
  tenantID: string
  environmentName: string
  pageID: number
  documentNumber: string
  /** The company's display *name*, which is what the web client's `company` parameter takes.
   *  Omitted on connections made before the name was captured — BC then opens the user's own
   *  default company, which is right for a single-company tenant and wrong for a multi-company
   *  one, so it is worth sending whenever it exists. */
  companyName?: string
}): string {
  if (!params.tenantID || !params.environmentName || !params.documentNumber) return ''
  const filter = encodeURIComponent(`'No.' IS '${params.documentNumber}'`)
  const company = params.companyName
    ? `company=${encodeURIComponent(params.companyName)}&`
    : ''
  return `https://businesscentral.dynamics.com/${encodeURIComponent(params.tenantID)}/${encodeURIComponent(params.environmentName)}?${company}page=${params.pageID}&filter=${filter}`
}

/**
 * Deep link to an invoice's document in BC: the posted invoice once it has a number, otherwise the
 * unposted draft by the number BC gave it at creation (`result.invoiceDraftNumber`). '' while there
 * is nothing to open — a pre-flight failure, or an invoice whose BC connection was never recorded.
 */
export function getBcInvoiceUrl(invoiceLike: {
  bcConnection?: { tenantID: string; environmentName: string; companyName?: string }
  result: { invoiceNumber?: string; invoiceDraftNumber?: string }
}): string {
  const isPosted = !!invoiceLike.result.invoiceNumber
  return buildBcDocumentUrl({
    tenantID: invoiceLike.bcConnection?.tenantID ?? '',
    environmentName: invoiceLike.bcConnection?.environmentName ?? '',
    companyName: invoiceLike.bcConnection?.companyName,
    pageID: isPosted ? BC_PAGE_POSTED_SALES_INVOICE : BC_PAGE_SALES_INVOICE,
    documentNumber: isPosted
      ? (invoiceLike.result.invoiceNumber ?? '')
      : (invoiceLike.result.invoiceDraftNumber ?? ''),
  })
}

/**
 * Deep link to the posted invoice a push refused to adopt (`[bc-foreign-invoice:<number>]` — BC
 * already holds a posted invoice with this order's reference). The number rides in the tag, not on
 * the invoice, because the failed invoice has no BC document of its own to link to. '' for any
 * other failure, or when the BC connection was never recorded.
 */
export function getBcErrorInvoiceUrl(
  error: string,
  bcConnection?: { tenantID: string; environmentName: string; companyName?: string }
): string {
  const parsed = parseBcErrorTag(error)
  const documentNumber = parsed.code === 'bc-foreign-invoice' ? (parsed.params[0] ?? '') : ''
  return buildBcDocumentUrl({
    tenantID: bcConnection?.tenantID ?? '',
    environmentName: bcConnection?.environmentName ?? '',
    companyName: bcConnection?.companyName,
    pageID: BC_PAGE_POSTED_SALES_INVOICE,
    documentNumber,
  })
}

/**
 * The request body for retrying a failed invoice. A retry repeats what was asked for the first time:
 * a draft that failed is retried as a draft. The backend reads an empty body as "send", which would
 * post the invoice and e-mail the customer from a retry of "Opret som kladde".
 */
export function getBcRetryBody(invoiceLike: { requestedAction?: string } | undefined): {
  postingMode?: 'draft'
} {
  return invoiceLike?.requestedAction === 'draft' ? { postingMode: 'draft' } : {}
}

export interface IBcPermissionSetRow {
  object: string
  type: 'Table Data' | 'Codeunit'
  id: string
  read: '' | 'Yes' | 'Indirect'
  insert: '' | 'Yes' | 'Indirect'
  modify: '' | 'Yes' | 'Indirect'
  delete: '' | 'Yes' | 'Indirect'
  execute: '' | 'Yes'
}

export const BC_PERMISSION_SET_ROLE_ID = 'MILVA LAB'
export const BC_PERMISSION_SET_ROLE_NAME = 'Milva Lab Integration'
export const BC_PERMISSION_SET_FILENAME = 'Milva Lab Permission Set.xml'

// The Base Application's App ID — the app that defines both built-in sets below.
export const BC_BASE_APPLICATION_APP_ID = '{437DBF0E-84FF-417A-965D-ED2BB9650972}'

// Built-in permission sets included in MILVA LAB via TenantPermissionSetRel on import, so one
// import + one assignment covers everything. Exact RoleIDs from the Base Application source
// (note the space in "D365 SALES DOC, POST" — permissionset 9977; "D365 READ" is 1044). A
// mismatched RoleID/AppID inserts a silently dangling relation, so never re-type these.
export const BC_INCLUDED_PERMISSION_SETS = ['D365 READ', 'D365 SALES DOC, POST'] as const

// Verified against a live BC tenant — see Business Central Integration.md "BC-Side Permission
// Set — Verified Requirements", which this list must stay in sync with. Object names are
// Business Central's own internal table/codeunit names, not translatable UI copy. The
// `Job Queue Log Entry` ID (474) was found via the permission set page's name-lookup in the
// live tenant (473, the standard-numbering guess, does not exist there); if an import fails on
// that row in another tenant/localization, look the table up by name there instead.
export const BC_CUSTOM_PERMISSION_SET_ROWS: readonly IBcPermissionSetRow[] = [
  { object: 'Item', type: 'Table Data', id: '27', read: '', insert: '', modify: 'Indirect', delete: '', execute: '' },
  { object: 'Sales Header', type: 'Table Data', id: '36', read: 'Yes', insert: 'Yes', modify: 'Yes', delete: 'Yes', execute: '' },
  { object: 'Sales Line', type: 'Table Data', id: '37', read: 'Yes', insert: 'Yes', modify: 'Yes', delete: 'Yes', execute: '' },
  { object: 'Job Queue Entry', type: 'Table Data', id: '472', read: 'Yes', insert: 'Yes', modify: 'Yes', delete: 'Yes', execute: '' },
  { object: 'Job Queue Log Entry', type: 'Table Data', id: '474', read: 'Yes', insert: 'Yes', modify: 'Yes', delete: 'Yes', execute: '' },
  { object: 'Error Message', type: 'Table Data', id: '700', read: 'Yes', insert: 'Yes', modify: 'Yes', delete: 'Yes', execute: '' },
  { object: 'Error Message Register', type: 'Table Data', id: '701', read: 'Yes', insert: 'Yes', modify: 'Yes', delete: 'Yes', execute: '' },
  { object: 'Sales Invoice Entity Aggregate', type: 'Table Data', id: '5475', read: 'Yes', insert: 'Indirect', modify: 'Indirect', delete: 'Indirect', execute: '' },
  { object: 'Sales Invoice Line Aggregate', type: 'Table Data', id: '5476', read: 'Yes', insert: 'Indirect', modify: 'Indirect', delete: 'Indirect', execute: '' },
  { object: 'Sales Cr. Memo Entity Buffer', type: 'Table Data', id: '5507', read: 'Yes', insert: 'Indirect', modify: 'Indirect', delete: 'Indirect', execute: '' },
  { object: 'Value Entry', type: 'Table Data', id: '5802', read: 'Yes', insert: 'Indirect', modify: '', delete: '', execute: '' },
  { object: 'Sales-Post', type: 'Codeunit', id: '80', read: '', insert: '', modify: '', delete: '', execute: 'Yes' },
]

// BC's permission XML values: 1 = direct (Yes), 2 = Indirect. Blank permissions are omitted
// entirely (mirroring BC's own export, which skips them), so 0 is never written.
function toBcPermissionValue(value: 'Yes' | 'Indirect'): 1 | 2 {
  return value === 'Yes' ? 1 : 2
}

/**
 * Builds the custom permission set as an XML document matching BC's own Export/Import
 * Permission Sets XmlPorts (9863/9864 in the System Application), so a BC admin can import it
 * directly (Permission Sets page → "Import Permission Sets") instead of re-typing the rows by
 * hand. The `Version` attribute and the `Assignable` attribute are schema-required (a file
 * without `Version` is rejected with "The required attribute 'Version' is missing"); BC's own
 * export stamps Version 2.0. Numeric option values (ObjectType 0 = Table Data, 5 = Codeunit;
 * permissions 1/2; relation Type 0 = Include, Scope 0 = System) are used instead of caption
 * text so the import evaluates identically regardless of the importing session's language.
 * The built-in `D365 READ` and `D365 SALES DOC, POST` sets are included as
 * `TenantPermissionSetRel` relations (BC's composable permission sets), so assigning the
 * imported `MILVA LAB` set alone grants the full union. Relations must come before the
 * `TenantPermission` elements — the XmlPort schema is order-sensitive.
 */
export function buildBcPermissionSetXml(): string {
  const relations = BC_INCLUDED_PERMISSION_SETS.map((roleID) =>
    [
      '    <TenantPermissionSetRel>',
      '      <ObjectType>0</ObjectType>',
      '      <ObjectRelatedScope>0</ObjectRelatedScope>',
      `      <ObjectRelatedRoleId>${roleID}</ObjectRelatedRoleId>`,
      `      <ObjectRelatedAppId>${BC_BASE_APPLICATION_APP_ID}</ObjectRelatedAppId>`,
      '    </TenantPermissionSetRel>',
    ].join('\n')
  ).join('\n')
  const permissions = BC_CUSTOM_PERMISSION_SET_ROWS.map((row) => {
    const lines = [
      '    <TenantPermission>',
      `      <ObjectType>${row.type === 'Codeunit' ? 5 : 0}</ObjectType>`,
      `      <ObjectID>${row.id}</ObjectID>`,
    ]
    if (row.read) lines.push(`      <ReadPermission>${toBcPermissionValue(row.read)}</ReadPermission>`)
    if (row.insert) lines.push(`      <InsertPermission>${toBcPermissionValue(row.insert)}</InsertPermission>`)
    if (row.modify) lines.push(`      <ModifyPermission>${toBcPermissionValue(row.modify)}</ModifyPermission>`)
    if (row.delete) lines.push(`      <DeletePermission>${toBcPermissionValue(row.delete)}</DeletePermission>`)
    if (row.execute) lines.push(`      <ExecutePermission>${toBcPermissionValue(row.execute)}</ExecutePermission>`)
    lines.push('    </TenantPermission>')
    return lines.join('\n')
  }).join('\n')
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="no"?>',
    '<PermissionSets Version="2.0">',
    `  <TenantPermissionSet RoleID="${BC_PERMISSION_SET_ROLE_ID}" RoleName="${BC_PERMISSION_SET_ROLE_NAME}" Assignable="true">`,
    relations,
    permissions,
    '  </TenantPermissionSet>',
    '</PermissionSets>',
    '',
  ].join('\n')
}

/** Hands the permission-set XML to the browser as a file download. */
export function downloadBcPermissionSetXml(): void {
  const blob = new Blob([buildBcPermissionSetXml()], { type: 'application/xml' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = BC_PERMISSION_SET_FILENAME
  link.click()
  URL.revokeObjectURL(url)
}
